// FILE: src/lib/user-service.ts
// VERSION: Modified with getCurrentUserProfile function for ChefCoin Assistant Integration

import { supabase } from './supabaseClient'
import { UserProfile } from '@/types/user'; // Import the new, correct UserProfile type from the types folder

// This local UserProfile interface is no longer needed as we now import it from a central location.
// The imported version from 'src/types/user.ts' is now the single source of truth.

export const getUsers = async (): Promise<UserProfile[]> => {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

export const updateUserRole = async (userId: string, role: 'admin' | 'reader' | 'guest'): Promise<void> => {
  // Route through the SECURITY DEFINER admin RPC — direct column writes to
  // user_profiles.role are revoked for clients (Phase 0 lockdown).
  const { error } = await supabase.rpc('admin_update_user', {
    p_user_id: userId,
    p_role: role,
  });

  if (error) {
    throw error;
  }
};

export const updateUserProfile = async (
  userId: string,
  profileData: { name?: string; country?: string }
): Promise<void> => {
  // Route through the SECURITY DEFINER self-service RPC — direct column writes
  // to user_profiles.role are revoked for clients (Phase 0 lockdown). The RPC
  // also promotes guest → reader automatically on completion.
  const { error } = await supabase.rpc('update_own_profile', {
    p_user_id: userId,
    p_name: profileData.name ?? null,
    p_country: profileData.country ?? null,
  });

  if (error) {
    throw error;
  }
};

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  // NO CHANGE NEEDED HERE. `select('*')` automatically includes all columns.
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    return null;
  }

  return data;
};

export const createGuestProfile = async (userId: string, email: string) => {
  const { error } = await supabase
    .from('user_profiles')
    .insert([{
      id: userId,
      email: email,
      role: 'guest'
    }]);

  if (error) throw error;
};

export const deleteUser = async (userId: string): Promise<void> => {
  const { data, error } = await supabase.functions.invoke('delete-user', {
    body: { userId },
  })

  if (error) {
    throw error
  }

  if (data && data.error) {
    throw new Error(data.error)
  }

};

// --- NEW FUNCTION FOR THE ASSISTANT ---
/**
 * Fetches the full user profile for the *currently authenticated user*.
 * This is the primary function the assistant context will use. It securely
 * gets the user's ID from the session and then fetches their profile data.
 * @returns The complete UserProfile object or null if an error occurs.
 */
export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return null;
  }

  // Use the existing getUserProfile function to avoid duplicating code.
  // This is efficient and keeps the logic centralized.
  return getUserProfile(user.id);
}